import { option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { useCallback, useMemo } from 'react';
import { MutateOptions, useMutation, UseMutationOptions, UseMutationResult } from 'react-query';

import { api, ApiError, ApiMethod, ApiRequest } from 'services/api';

export type Mutate<Response, Body = any> = (
  variables?: Body,
  options?: MutateOptions<Response, unknown, ApiRequest>,
  dynamicURL?: string,
) => void;

export type RestMutateHookResult<Response = any, Body = any> = Omit<
  UseMutationResult<Response, unknown, ApiRequest>,
  'mutate'
> & {
  mutate: Mutate<Response, Body>;
};

export type AllowedMutationMethods =
  | ApiMethod.delete
  | ApiMethod.patch
  | ApiMethod.post
  | ApiMethod.put;

export type UseRestMutationOptions<Response = any> = Omit<
  UseMutationOptions<Response | undefined, unknown, ApiRequest>,
  'mutationFn'
>;

/**
 * Restful Mutation Hook
 *
 * Wrapped REST mutation requests with some abstractions.
 * @param url Target Endpoint URL
 * @param method Which REST verb to use (default is POST)
 * @param baseOptions Additional React-Query options
 * @param config Additional request config
 * @returns Response object
 */
export function useRestMutation<Response = any, Body = any>(
  url?: string,
  method: AllowedMutationMethods = ApiMethod.post,
  baseOptions?: UseRestMutationOptions<Response>,
  config?: ApiRequest,
): RestMutateHookResult<Response, Body> {
  const initialConfig = useMemo(
    () => config,
    [], // We don't want to update `useLazyApyRequest` hook if config changes
  );

  const { mutate: request, ...result } = useMutation<Response, ApiError, ApiRequest>(
    (params: ApiRequest = {}) =>
      api
        .request<Response>({
          ...initialConfig,
          url,
          method,
          ...params,
        })
        .then(({ data }) => data),
    baseOptions,
  );

  const mutate: Mutate<Response, Body> = useCallback(
    (
      variables?: Body,
      options?: MutateOptions<Response, ApiError, ApiRequest>,
      dynamicURL?: string,
    ) => {
      if (!dynamicURL && !url) {
        return;
      }

      /**
       * If dynamicURL is provided, we need to override the URL.
       * This is useful for endpoints that require a non-static URL (e.g., /projects/:id).
       */
      const dynamicUrlParams = dynamicURL ? { url: dynamicURL } : {};

      const requestConfig = pipe(
        variables,
        option.fromNullable,
        option.map((v) =>
          method === ApiMethod.delete
            ? { params: v, ...dynamicUrlParams }
            : { data: v, ...dynamicUrlParams },
        ),
        option.getOrElse(() => ({})),
      );

      request(requestConfig, options);
    },
    [url, method, request],
  );

  return {
    ...result,
    mutate,
  };
}
